// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — read the pins on the frame the video is showing (2026-10-10).
 *
 * The pins used to be read on a 200 ms timer, stamped with the video's
 * `currentTime`. The probe uploads whatever picture the video holds at
 * that moment, and the two do not always agree:
 *
 *  - **During a seek** (the loop's wrap to frame 0 is one, so is a click on
 *    the graph or a drag on the date track) `currentTime` already names
 *    the target while the picture is still the old frame. A read then
 *    files the old frame's value under the new one, and since the frame
 *    id does not change again, nothing corrects it until a later lap does.
 *  - **While playing, and on pausing,** the media clock and the picture
 *    can be a frame apart: measured 2026-10-10 in Chromium, 3 reads in 7
 *    had the frame before or after the one `currentTime` named.
 *
 * Either way a slot holds a neighbour's value on one lap and its own on
 * the next, and the line changes. So the pins are read on frames that
 * `requestVideoFrameCallback` reports shown, under that frame's
 * `mediaTime`: inside the callback while playing (at most every 200 ms)
 * and after a paused seek lands; on the timer while paused, on the frame
 * last reported (the store reads it once, or again when the pins change).
 * After a seek nothing is read until its frame is reported. Where the
 * callbacks stop (an immersive WebXR session renders outside the
 * window's frames) or do not exist, the timer reads by `currentTime`, as
 * it always did.
 */

/** The parts of a video element this needs (a test passes a stand-in). */
export interface SyncVideo {
  readonly paused: boolean
  readonly seeking: boolean
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number
  addEventListener(type: 'seeking', listener: () => void): void
  removeEventListener(type: 'seeking', listener: () => void): void
}

/** Read the pins: on the frame shown at `mediaTime`, or by `currentTime` when null. */
export type FrameRead = (mediaTime: number | null) => void

export interface FrameSync {
  /** Follow a video's frames (the dataset's; null for a still image). Cheap to call every tick. */
  watch(video: SyncVideo | null | undefined): void
  /** The 200 ms timer: reads the pins when it is the one to (see the module's comment). */
  tick(video: SyncVideo | null | undefined): void
  dispose(): void
}

export interface FrameSyncOptions {
  /** Least time between two reads while playing (the timer's own). */
  readonly intervalMs?: number
  /** Callbacks this old mean they stopped: the timer reads by `currentTime` again. */
  readonly staleMs?: number
  readonly now?: () => number
}

export function createFrameSync(read: FrameRead, options: FrameSyncOptions = {}): FrameSync {
  const intervalMs = options.intervalMs ?? 200
  const staleMs = options.staleMs ?? 600
  const now = options.now ?? (() => performance.now())
  let watched: SyncVideo | null = null
  let lastCallback = -Infinity
  let lastRead = -Infinity
  /** The frame last reported shown, and how many seeks had begun by then. */
  let shown: number | null = null
  let shownAtSeek = 0
  let seeks = 0
  const onSeeking = (): void => { seeks++ }

  const listen = (video: SyncVideo): void => {
    const onVideoFrame = (_now: number, meta: { mediaTime: number }): void => {
      if (watched !== video) return
      lastCallback = now()
      shown = meta.mediaTime
      shownAtSeek = seeks
      video.requestVideoFrameCallback!(onVideoFrame)
      if (video.seeking) return
      // Ten milliseconds' slack: callbacks come on the display's beat, not the timer's.
      if (!video.paused && lastCallback - lastRead < intervalMs - 10) return
      lastRead = lastCallback
      read(shown)
    }
    video.requestVideoFrameCallback!(onVideoFrame)
  }

  return {
    watch(video) {
      const next = video ?? null
      if (next === watched) return
      watched?.removeEventListener('seeking', onSeeking)
      watched = next
      lastCallback = -Infinity
      shown = null
      if (!next) return
      next.addEventListener('seeking', onSeeking)
      if (typeof next.requestVideoFrameCallback === 'function') listen(next)
    },
    tick(video) {
      if (!video) { read(null); return }
      if (video.seeking) return
      if (typeof video.requestVideoFrameCallback !== 'function') { read(null); return }
      const stale = now() - lastCallback > staleMs
      if (!video.paused) {
        if (stale) read(null)
        return
      }
      // Paused on the frame last reported, with no seek since: read it (the store skips a frame it has).
      if (shown !== null && shownAtSeek === seeks) read(shown)
      else if (stale) read(null)
    },
    dispose() {
      watched?.removeEventListener('seeking', onSeeking)
      watched = null
    },
  }
}
